import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { ChevronLeft, ChevronRight, GitBranch } from 'lucide-react';
import Tooltip from './Tooltip';
import styles from './BranchNavigator.module.css';

interface Branch {
  versionId: string;
  versionNumber: number;
  name: string;
  parentVersionId?: string;
  branchFromMessageId?: string;
  branchFromMessageIndex?: number;
  createdAt: string;
  isCurrentVersion: boolean;
}

interface BranchNavigatorProps {
  currentVersionId: string | null;
  onBranchSwitch: (versionId: string) => void;
}

const BranchNavigator: React.FC<BranchNavigatorProps> = ({
  currentVersionId,
  onBranchSwitch
}) => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [currentBranchIndex, setCurrentBranchIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Load branches when version changes
  useEffect(() => {
    if (currentVersionId) {
      loadBranches();
    }
  }, [currentVersionId]);

  const loadBranches = async () => {
    if (!currentVersionId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.getResumeBranches(currentVersionId);
      
      if (response.branches && response.branches.length > 1) {
        // Only show navigator if there are multiple branches
        const sortedBranches = response.branches.sort((a: Branch, b: Branch) => 
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
        
        setBranches(sortedBranches);
        
        // Find current branch index
        const currentIndex = sortedBranches.findIndex((b: Branch) => b.versionId === currentVersionId);
        setCurrentBranchIndex(currentIndex >= 0 ? currentIndex : 0);
        
        setIsVisible(true);
      } else {
        // Hide navigator if only one branch
        setIsVisible(false);
      }
    } catch (err) {
      console.error('Error loading branches:', err);
      setError('Failed to load branches');
      setIsVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousBranch = () => {
    if (currentBranchIndex > 0) {
      const newIndex = currentBranchIndex - 1;
      setCurrentBranchIndex(newIndex);
      onBranchSwitch(branches[newIndex].versionId);
    }
  };

  const handleNextBranch = () => {
    if (currentBranchIndex < branches.length - 1) {
      const newIndex = currentBranchIndex + 1;
      setCurrentBranchIndex(newIndex);
      onBranchSwitch(branches[newIndex].versionId);
    }
  };

  if (!isVisible || loading || branches.length <= 1) {
    return null;
  }

  const currentBranch = branches[currentBranchIndex];
  
  return (
    <div className={styles.branchNavigator}>
      <Tooltip content="Go to previous branch">
        <button
          className={styles.navButton}
          onClick={handlePreviousBranch}
          disabled={currentBranchIndex === 0}
          aria-label="Previous branch"
        >
          <ChevronLeft size={16} />
        </button>
      </Tooltip>
      
      <div className={styles.branchInfo}>
        <GitBranch size={14} className={styles.branchIcon} />
        <span className={styles.branchText}>
          Branch {currentBranchIndex + 1} of {branches.length}
        </span>
        {currentBranch?.branchFromMessageIndex !== undefined && (
          <span className={styles.branchDetail}>
            (from message {currentBranch.branchFromMessageIndex + 1})
          </span>
        )}
      </div>
      
      <Tooltip content="Go to next branch">
        <button
          className={styles.navButton}
          onClick={handleNextBranch}
          disabled={currentBranchIndex === branches.length - 1}
          aria-label="Next branch"
        >
          <ChevronRight size={16} />
        </button>
      </Tooltip>
    </div>
  );
};

export default BranchNavigator;